import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gem, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { applySession } = useAuth();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.a.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw.a !== pw.b) return setErr("The two passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw.a });
      if (data?.token && applySession) applySession(data.token, data.user);
      nav(data?.token ? "/" : "/login", { replace: true });
    } catch (e2) {
      setErr(apiErr(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Gem className="h-7 w-7 text-primary" strokeWidth={1.5} />
          <span className="font-display font-extrabold text-xl">Jewel<span className="text-primary">tix</span></span>
        </Link>
        {!token ? (
          <div className="border border-border rounded-md bg-card p-6" data-testid="reset-no-token">
            <h1 className="font-display font-bold text-xl">Link looks incomplete</h1>
            <p className="text-sm text-muted-foreground mt-2">Open the reset link straight from your email, or request a new one.</p>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm text-primary font-semibold">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border rounded-md bg-card p-6">
            <h1 className="font-display font-bold text-2xl">Set a new password</h1>
            <p className="text-sm text-muted-foreground mt-1">Pick something you haven't used elsewhere.</p>
            <label className="block mt-5">
              <span className="text-sm font-medium">New password</span>
              <input type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-new-pw"
                className="mt-1 w-full border border-border rounded-md p-2.5 text-base bg-background focus:outline-none focus:border-primary" />
            </label>
            <label className="block mt-3">
              <span className="text-sm font-medium">Confirm new password</span>
              <input type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-confirm-pw"
                className="mt-1 w-full border border-border rounded-md p-2.5 text-base bg-background focus:outline-none focus:border-primary" />
            </label>
            {err && <p className="text-sm text-destructive mt-3" data-testid="reset-error">{err}</p>}
            <button
              disabled={busy}
              data-testid="reset-submit"
              className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save new password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
